import { EmployeeAvatar } from '@newfront-insurance/core-ui';
import { Button, Checkbox, cn, IconCheckmark, IconSearch, Skeleton } from '@newfront-insurance/core-ui/v2';
import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ClipboardEvent, type KeyboardEvent, type UIEvent } from 'react';
import { useDebounce } from 'react-use';

import {
  getEmailAddresses,
  getEmployeeDisplayName,
  getEmployeeInitials,
  type SettingsEmployee,
} from './employee-utils';
import type { PeoplePickerProps } from './people-picker';
import { MAX_PASTED_EMAILS, MAX_SELECT_ALL, useSkillGroupPeople } from './use-skill-group-people';

const SEARCH_DEBOUNCE_MS = 200;
const LOAD_MORE_THRESHOLD_PX = 160;
// Button's default size (h-10 px-8 py-3) outranks the inline variant's own reset, so inline links would stretch the
// rows they sit in. Size them like the text around them instead.
const INLINE_BUTTON_CLASS = 'h-auto p-0 text-xs';

/** A label for everyone locked, or one per person when they're locked for different reasons. */
export type ExcludedLabel = string | ((userUuid: string) => string);

export interface OrganizationPeoplePickerProps extends PeoplePickerProps {
  organizationUuid: string | null;
  /** Shown beside people in `excludedUserUuids`, who appear ticked and locked. */
  excludedLabel?: ExcludedLabel;
  /**
   * Choose one person. The list shows radio buttons, and Select all, the Selected tab and pasted email lists are
   * hidden. `onAddEmployee` must replace the selection rather than add to it. People in `excludedUserUuids` appear
   * locked but not chosen.
   */
  single?: boolean;
}

const countPeople = (count: number) => `${count} ${count === 1 ? 'person' : 'people'}`;
const countResults = (count: number) => `${count} ${count === 1 ? 'result' : 'results'}`;

// The Selected tab filters on the client: a list of emails keeps those people, and other text uses the same word-start
// rule the BFF uses for the full list.
function matchesSearch(employee: SettingsEmployee, text: string) {
  const email = employee.email.toLowerCase();
  const emails = getEmailAddresses(text);
  if (emails.length > 1) return emails.includes(email);
  const terms = text.toLowerCase().split(/\s+/).filter(Boolean);
  const words = `${getEmployeeDisplayName(employee)} ${email.split('@')[0]}`
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  return terms.every((term) => email.startsWith(term) || words.some((word) => word.startsWith(term)));
}

/**
 * Lists everyone in an organization so admins can browse instead of recalling names. The BFF scopes, searches and
 * pages the list. A pasted list of emails narrows it to those people, ready for "Select all".
 */
export function OrganizationPeoplePicker({
  organizationUuid,
  excludedUserUuids,
  excludedLabel = 'Already added',
  inputId,
  selectedEmployees,
  onAddEmployee,
  onRemoveEmployee,
  disabled = false,
  single = false,
}: OrganizationPeoplePickerProps) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const getExcludedLabel = (userUuid: string) =>
    typeof excludedLabel === 'function' ? excludedLabel(userUuid) : excludedLabel;
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState<'all' | 'selected'>('all');
  const [notice, setNotice] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [activeUserUuid, setActiveUserUuid] = useState<string | null>(null);
  const active = useRef(true);
  useDebounce(() => setSearchQuery(query.trim()), SEARCH_DEBOUNCE_MS, [query]);
  const selectedUserUuids = new Set(selectedEmployees.map((employee) => employee.userUuid));
  const isSelectable = (employee: SettingsEmployee) =>
    !excludedUserUuids.has(employee.userUuid) && !selectedUserUuids.has(employee.userUuid);
  // Two or more emails list just those people. A single email is an ordinary search.
  const searchEmails = getEmailAddresses(searchQuery);
  const isEmailSearch = searchEmails.length > 1;
  const people = useSkillGroupPeople({
    groupId: organizationUuid,
    query: isEmailSearch ? '' : searchQuery,
    emails: isEmailSearch ? searchEmails.slice(0, MAX_PASTED_EMAILS) : null,
  });

  // Changing organization remounts this picker. Lookups still in flight must not add the old organization's people.
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const isSearchPending = query.trim() !== searchQuery;
  const isBusy = isSearchPending || people.isLoading;
  const isStale = view === 'all' && isBusy && people.people.length > 0;
  const visibleEmployees =
    view === 'all' ? people.people : selectedEmployees.filter((employee) => matchesSearch(employee, query));
  const loadedSelectable = people.people.filter((employee) => !excludedUserUuids.has(employee.userUuid));
  const selectedLoadedCount = loadedSelectable.filter((employee) => selectedUserUuids.has(employee.userUuid)).length;
  const allResultsSelected =
    !people.hasMore && loadedSelectable.length > 0 && selectedLoadedCount === loadedSelectable.length;
  const showsError = view === 'all' && people.error;
  const showsSkeleton = view === 'all' && isBusy && !people.people.length;
  const showsList = Boolean(organizationUuid) && !showsError && !showsSkeleton && visibleEmployees.length > 0;
  // The highlighted row is what Enter acts on. It starts at the top and follows the arrow keys.
  const activeEmployee =
    isInputFocused && showsList
      ? (visibleEmployees.find((employee) => employee.userUuid === activeUserUuid) ?? visibleEmployees[0])
      : undefined;
  const optionId = (employee: SettingsEmployee) => `${listId}-${employee.userUuid}`;

  const toggle = (employee: SettingsEmployee) => {
    if (disabled || isStale || excludedUserUuids.has(employee.userUuid)) return;
    if (selectedUserUuids.has(employee.userUuid)) onRemoveEmployee(employee.userUuid);
    else onAddEmployee(employee);
  };

  const moveHighlight = (step: number) => {
    if (!activeEmployee) return;
    const index = visibleEmployees.indexOf(activeEmployee) + step;
    const next = visibleEmployees[Math.max(0, Math.min(visibleEmployees.length - 1, index))];
    setActiveUserUuid(next.userUuid);
    document.getElementById(optionId(next))?.scrollIntoView({ block: 'nearest' });
  };

  // The list still shows an earlier search, so fetch the top match for what was typed.
  const addTopMatch = async (text: string) => {
    try {
      const match = (await people.searchPeople(text)).find(isSelectable);
      if (match && active.current) onAddEmployee(match);
    } catch {
      // The list shows the error when its own request for this search fails.
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key !== 'Enter') return;
    // Picking a person must not submit the surrounding form.
    event.preventDefault();
    if (view === 'all' && isBusy) {
      if (query.trim() && getEmailAddresses(query).length < 2) void addTopMatch(query.trim());
    } else if (activeEmployee) {
      toggle(activeEmployee);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    if (single) return;
    const pastedEmails = getEmailAddresses(event.clipboardData.getData('text'));
    const emails = [...new Set([...getEmailAddresses(query), ...pastedEmails])];
    if (!pastedEmails.length || emails.length < 2) return;
    // A single-line input drops line breaks, which would run a pasted column of emails together.
    event.preventDefault();
    setQuery(emails.join(', '));
    setSearchQuery(emails.join(', '));
    setActiveUserUuid(null);
  };

  const toggleAllResults = async (isChecked: boolean) => {
    if (!isChecked) {
      people.people.forEach((employee) => {
        if (selectedUserUuids.has(employee.userUuid)) onRemoveEmployee(employee.userUuid);
      });
      return;
    }
    // Only some results may be loaded, so fetch the rest before selecting.
    setNotice(null);
    try {
      const results = await people.loadAll();
      if (active.current) results?.filter(isSelectable).forEach(onAddEmployee);
    } catch {
      setNotice("Couldn't select all results. Try again.");
    }
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    if (view === 'all' && list.scrollTop + list.clientHeight >= list.scrollHeight - LOAD_MORE_THRESHOLD_PX) {
      people.loadMore();
    }
  };

  const emailNotes =
    isEmailSearch && !isBusy
      ? [
          people.unmatchedEmails.length ? `Not found in this organization: ${people.unmatchedEmails.join(', ')}` : '',
          searchEmails.length > MAX_PASTED_EMAILS ? `Only the first ${MAX_PASTED_EMAILS} emails are listed.` : '',
        ].filter(Boolean)
      : [];

  const renderListHeader = () => {
    if (view === 'selected') {
      return selectedEmployees.length ? 'Untick anyone you want to remove.' : 'No one selected yet.';
    }
    if (!organizationUuid) return 'Choose an organization to see its people.';
    if (people.isLoadingAll) return `Selecting ${countResults(people.totalCount)}…`;
    if (isBusy) return query.trim() ? 'Searching…' : 'Loading people…';
    if (people.error) return null;
    if (!searchQuery) return `${countPeople(people.totalCount)} in this organization`;
    if (!people.totalCount) return 'No matches';
    if (single) return countResults(people.totalCount);
    if (people.totalCount > MAX_SELECT_ALL) {
      return `${countResults(people.totalCount)}. Narrow your search to select them all at once.`;
    }
    let checked: boolean | 'indeterminate' = false;
    if (allResultsSelected) checked = true;
    else if (selectedLoadedCount) checked = 'indeterminate';
    return (
      <label className="text-steel-500 flex cursor-pointer items-center gap-3 font-semibold">
        <Checkbox
          className="size-4"
          checked={checked}
          disabled={disabled}
          onCheckedChange={(next) => void toggleAllResults(next === true)}
        />
        {allResultsSelected
          ? `All ${countResults(people.totalCount)} selected`
          : `Select all ${countResults(people.totalCount)}`}
      </label>
    );
  };

  const renderListFooter = () => {
    if (view !== 'all' || isStale) return null;
    if (people.isLoadingMore) {
      return <p className="text-steel-400 px-3 py-3 text-center text-xs">Loading more people…</p>;
    }
    if (people.loadMoreError) {
      return (
        <p role="alert" className="text-fire-500 px-3 py-3 text-center text-xs">
          Couldn't load more people.{' '}
          <Button type="button" variant="inline" className={INLINE_BUTTON_CLASS} onClick={people.loadMore}>
            Retry
          </Button>
        </p>
      );
    }
    if (people.hasMore) {
      return (
        <p className="text-steel-400 px-3 py-3 text-center text-xs">
          Showing {people.people.length} of {people.totalCount}.{' '}
          <Button
            type="button"
            variant="inline"
            className={INLINE_BUTTON_CLASS}
            disabled={people.isLoadingAll}
            onClick={people.loadMore}
          >
            Load more
          </Button>
        </p>
      );
    }
    return null;
  };

  const renderList = () => {
    if (!organizationUuid) return null;
    if (showsError) {
      return (
        <p role="alert" className="text-fire-500 px-3 py-4 text-sm">
          Couldn't load people.{' '}
          <Button type="button" variant="inline" className={cn(INLINE_BUTTON_CLASS, 'text-sm')} onClick={people.retry}>
            Retry
          </Button>
        </p>
      );
    }
    if (showsSkeleton) {
      return (
        <div role="status" aria-label="Loading people" className="space-y-4 px-3 py-4">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="bg-steel-100 h-8 w-full" />
          ))}
        </div>
      );
    }
    if (!visibleEmployees.length) {
      let message = 'No one in this organization matches your search.';
      if (view === 'selected' && !query.trim()) message = 'Switch to Everyone to pick people.';
      else if (view === 'all' && !searchQuery) message = 'No one belongs to this organization yet.';
      return <p className="text-steel-400 px-3 py-4 text-sm">{message}</p>;
    }
    return (
      <>
        <ul
          id={listId}
          role="listbox"
          aria-multiselectable={!single}
          aria-label={view === 'all' ? 'People in this organization' : 'Selected people'}
        >
          {visibleEmployees.map((employee) => {
            const name = getEmployeeDisplayName(employee);
            // Someone without a name on their profile is shown by email, so it isn't repeated underneath.
            const showsEmail = name.toLowerCase() !== employee.email.toLowerCase();
            const isLocked = excludedUserUuids.has(employee.userUuid);
            const lockedLabel = isLocked ? getExcludedLabel(employee.userUuid) : null;
            const isChecked = (isLocked && !single) || selectedUserUuids.has(employee.userUuid);
            const isDisabled = disabled || isLocked || isStale;
            return (
              <li
                key={employee.userUuid}
                id={optionId(employee)}
                role="option"
                aria-label={[name, showsEmail && employee.email, lockedLabel].filter(Boolean).join(', ')}
                aria-selected={isChecked}
                aria-disabled={isDisabled}
                className={cn(
                  'border-steel-75 hover:bg-steel-50 flex min-h-14 cursor-pointer items-center gap-3 border-b px-3 py-2 last:border-b-0',
                  isDisabled && 'cursor-default',
                  employee === activeEmployee && 'bg-steel-50',
                )}
                // Keep typing focus in the search box, where the arrow keys and Enter work.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setActiveUserUuid(employee.userUuid);
                  toggle(employee);
                }}
              >
                {/* Drawn like Checkbox but not a form control: the row is the option, and inside a form Radix's
                    hidden checkbox input re-dispatches clicks that would bubble to the row and toggle it again. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'border-wtw-graymatter-200 flex size-4 shrink-0 items-center justify-center border',
                    single ? 'rounded-full' : 'rounded-xs',
                    isChecked && 'border-wtw-stratosphere-450 bg-wtw-stratosphere-450 text-white',
                    isDisabled && 'opacity-50',
                  )}
                >
                  {isChecked && single ? <span className="size-1.5 rounded-full bg-white" /> : null}
                  {isChecked && !single ? <IconCheckmark className="size-4" /> : null}
                </span>
                <EmployeeAvatar
                  sizeInPixels={32}
                  imageUrl={employee.imageUrl}
                  initials={getEmployeeInitials(employee, employee.userUuid)}
                />
                <span className="min-w-0 flex-1">
                  <span className="text-steel-500 block truncate text-sm font-semibold">{name}</span>
                  {showsEmail ? <span className="text-steel-400 block truncate text-xs">{employee.email}</span> : null}
                </span>
                {lockedLabel ? <span className="text-steel-400 shrink-0 text-xs">{lockedLabel}</span> : null}
              </li>
            );
          })}
        </ul>
        {renderListFooter()}
      </>
    );
  };

  return (
    <div className="border-steel-200 focus-within:border-brand-400 rounded-[4px] border bg-white">
      <div className="border-steel-75 flex h-10 items-center gap-2 border-b px-3">
        <IconSearch aria-hidden="true" className="text-steel-300 size-4 shrink-0" />
        <input
          id={inputId}
          type="search"
          role="combobox"
          aria-label="Search people by name or email"
          aria-expanded={showsList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeEmployee ? optionId(activeEmployee) : undefined}
          autoComplete="off"
          disabled={disabled || !organizationUuid}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveUserUuid(null);
          }}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={single ? 'Search by name or email' : 'Search by name or email, or paste a list of emails'}
          className="text-steel-500 placeholder:text-steel-300 h-full min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
        />
      </div>
      <div className="border-steel-75 bg-steel-50 flex min-h-11 items-center justify-between gap-3 border-b px-3 py-2">
        {single ? (
          <p className="text-steel-500 min-w-0 truncate text-xs font-semibold">
            {selectedEmployees[0] ? `${getEmployeeDisplayName(selectedEmployees[0])} selected` : 'Choose one person.'}
          </p>
        ) : (
          <div role="group" aria-label="Show" className="bg-steel-100 inline-flex rounded-full p-0.5">
            {(['all', 'selected'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={view === option}
                onClick={() => {
                  setView(option);
                  setActiveUserUuid(null);
                }}
                className={cn(
                  'text-steel-400 rounded-full px-3 py-0.5 text-xs font-semibold',
                  view === option && 'text-steel-500 bg-white shadow-sm',
                )}
              >
                {option === 'all' ? 'Everyone' : `Selected (${selectedEmployees.length})`}
              </button>
            ))}
          </div>
        )}
        {selectedEmployees.length && !single ? (
          <span className="text-steel-500 flex items-center gap-2 text-xs font-semibold">
            {countPeople(selectedEmployees.length)} selected
            <Button
              type="button"
              variant="inline"
              className={INLINE_BUTTON_CLASS}
              disabled={disabled}
              onClick={() => selectedEmployees.forEach((employee) => onRemoveEmployee(employee.userUuid))}
            >
              Clear
            </Button>
          </span>
        ) : null}
      </div>
      {emailNotes.length ? (
        <div
          role="status"
          className="border-steel-75 bg-glitter-200 text-steel-500 border-b px-3 py-2 text-xs break-words"
        >
          {emailNotes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      ) : null}
      {notice ? (
        <div role="status" className="border-steel-75 bg-steel-50 flex items-start gap-2 border-b px-3 py-2 text-xs">
          <p className="text-steel-500 min-w-0 flex-1 break-words">{notice}</p>
          <button
            type="button"
            aria-label="Dismiss"
            className="text-steel-400 hover:text-steel-500 shrink-0"
            onClick={() => setNotice(null)}
          >
            <X aria-hidden="true" className="size-3.5" />
          </button>
        </div>
      ) : null}
      <div className="border-steel-75 text-steel-400 flex min-h-9 items-center border-b px-3 py-1.5 text-xs">
        {renderListHeader()}
      </div>
      <div
        aria-busy={view === 'all' && isBusy}
        onScroll={handleScroll}
        // A fixed height, so the dialog doesn't resize as results change.
        className={cn('h-72 overflow-y-auto', isStale && 'opacity-60')}
      >
        {renderList()}
      </div>
    </div>
  );
}
